import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthWrapper } from './components/AuthWrapper';
import { RouteGuard } from './components/RouteGuard';
import { InitialRoute } from './components/InitialRoute';
import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { DevTools } from './components/DevTools';
import ErrorBoundary from './components/ErrorBoundary';
import { NetworkStatus } from './components/NetworkStatus';
import { CommandPalette } from './components/CommandPalette';
import { AriaAnnouncer } from './components/AriaAnnouncer';
import { SkipToContent } from './components/AccessibleComponents';
import { useGlobalKeyboardShortcuts, useFocusVisible } from './hooks/useKeyboardNavigation';
import { useA11yChecklist } from './hooks/useAccessibility';
import { ThemeProvider } from './hooks/useDarkMode';
// import { OfflineIndicator } from './components/OfflineIndicator'; // Disabled due to IndexedDB errors
import { QuestionDataLocalStorage } from './lib/localStorage/questionDataLocalStorage';
import { useUIStore } from './store/useUIStore';
import { WebViewProvider, useWebView } from './integrations/webview';
import './App.css';
import './utils/consoleLogger';
import './utils/capacitorAudioFix';
import './utils/testAudio';

// Direct imports to avoid lazy loading issues
import { OrgCodePage } from './pages/OrgCodePage';
import { LoginPage } from './pages/LoginPage';
import LibraryPage from './pages/LibraryPage';
import WhiteboardLayout from './components/WhiteboardLayout';
import { WebViewWhiteboardWrapper } from './components/WebViewWhiteboardWrapper';
import { WebViewDebug } from './components/WebViewDebug';
import PlayerPage from './pages/PlayerPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ProfilePage from './pages/ProfilePage';
import ApiTest from './pages/ApiTest';
import SimpleTest from './pages/SimpleTest';



// Lazy load test pages only
const TestExternalProps = lazy(() => import('./pages/TestExternalProps'));
const TestExport = lazy(() => import('./pages/TestExport'));
const StandaloneWhiteboardV2 = lazy(() => import('./components/StandaloneWhiteboardV2'));
const TestOpticalAnswers = lazy(() => import('./pages/TestOpticalAnswers'));
const TestRecordingProps = lazy(() => import('./pages/TestRecordingProps'));
const TestSolutionViewer = lazy(() => import('./pages/TestSolutionViewer'));
const UnifiedTestPage = lazy(() => import('./pages/UnifiedTestPage'));
const TestWhiteboardContainer = lazy(() => import('./pages/TestWhiteboardContainer'));
const TestWhiteboardDebug = lazy(() => import('./pages/TestWhiteboardDebug'));
const TestOpticalAndStrokes = lazy(() => import('./pages/TestOpticalAndStrokes'));
const TestSelectionHistory = lazy(() => import('./pages/TestSelectionHistory').then(module => ({ default: module.TestSelectionHistory })));
const TestSelectionHistoryFlow = lazy(() => import('./pages/TestSelectionHistoryFlow'));
const TestGlassEffects = lazy(() => import('./pages/TestGlassEffects'));
const TestWebView = lazy(() => import('./pages/TestWebView'));
const TestIntervalTracking = lazy(() => import('./pages/TestIntervalTracking'));
const TestWebViewIntervals = lazy(() => import('./pages/TestWebViewIntervals'));
const WebViewDemo = lazy(() => import('./screens/WhiteboardWebView/WebViewDemo'));
const TestIOSPen = lazy(() => import('./pages/TestIOSPen'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

// Component that uses router context
const AppContent: React.FC = () => {
  // Initialize keyboard navigation (needs to be inside Router)
  useGlobalKeyboardShortcuts();
  
  // Check if we're in WebView context (after Router is available)
  const { isWebView, externalProps } = useWebView();
  
  // Debug WebView detection
  useEffect(() => {
    console.log('AppContent - WebView detection:', {
      isWebView,
      hasExternalProps: !!externalProps,
      pathname: window.location.pathname,
      search: window.location.search
    });
  }, [isWebView, externalProps]);
  
  // If we have external props (WebView mode), go directly to whiteboard
  if (isWebView && externalProps) {
    console.log('AppContent - Rendering WebViewWhiteboardWrapper directly');
    return (
      <AuthWrapper>
        <main id="main-content">
          <WebViewWhiteboardWrapper />
        </main>
      </AuthWrapper>
    );
  }
  
  return (
    <AuthWrapper>
      <DevTools />
      <NetworkStatus />
      <CommandPalette />
      <AriaAnnouncer />
      {/* <OfflineIndicator /> */} {/* Disabled due to IndexedDB errors */}
      {isWebView && <WebViewDebug />}
      <main id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<InitialRoute />} />
            <Route path="/test" element={<StandaloneWhiteboardV2 />} />
            <Route path="/auth" element={<InitialRoute />} />
            <Route path="/webview" element={<WebViewWhiteboardWrapper />} />
            <Route
              path="/org-code"
              element={
                <RouteGuard requireAuth={false} requireTenant={false} isPublic={true}>
                  <OrgCodePage />
                </RouteGuard>
              }
            />
            <Route
              path="/login"
              element={
                <RouteGuard requireAuth={false} requireTenant={true} isPublic={true}>
                  <LoginPage />
                </RouteGuard>
              }
            />

            {/* Protected routes */}
            <Route
              path="/library"
              element={
                <RouteGuard>
                  <LibraryPage />
                </RouteGuard>
              }
            />
            <Route
              path="/whiteboard"
              element={
                <RouteGuard>
                  {isWebView ? <WebViewWhiteboardWrapper /> : <WhiteboardLayout />}
                </RouteGuard>
              }
            />
            <Route
              path="/whiteboard/:testId"
              element={
                <RouteGuard>
                  {isWebView ? <WebViewWhiteboardWrapper /> : <WhiteboardLayout />}
                </RouteGuard>
              }
            />
            <Route
              path="/player/:id"
              element={
                <RouteGuard requireAuth={false} requireTenant={false} isPublic={true}>
                  <PlayerPage />
                </RouteGuard>
              }
            />
            <Route
              path="/player"
              element={
                <RouteGuard>
                  <PlayerPage />
                </RouteGuard>
              }
            />
            
            {/* Policy Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Profile Page */}
            <Route
              path="/profile"
              element={
                <RouteGuard>
                  <ProfilePage />
                </RouteGuard>
              }
            />
            
            {/* API Test Page */}
            <Route path="/sincapp-api" element={<ApiTest />} />
            <Route path="/simple-test" element={<SimpleTest />} />
            
            {/* Unified Test Page - Main test route */}
            <Route path="/test-unified" element={<UnifiedTestPage />} />
            
            {/* Legacy test routes - consider deprecating */}
            <Route path="/test-external" element={<TestExternalProps />} />
            <Route path="/test-export" element={<TestExport />} />
            <Route path="/test-optical" element={<TestOpticalAnswers />} />
            <Route path="/test-recording-props" element={<TestRecordingProps />} />
            <Route path="/test-solution-viewer" element={<TestSolutionViewer />} />
            
            {/* New whiteboard test routes */}
            <Route path="/test-whiteboard-container" element={<TestWhiteboardContainer />} />
            <Route path="/test-whiteboard-debug" element={<TestWhiteboardDebug />} />
            <Route path="/test-optical-strokes" element={<TestOpticalAndStrokes />} />
            <Route path="/test-selection-history" element={<TestSelectionHistory />} />
            <Route path="/test-selection-flow" element={<TestSelectionHistoryFlow />} />
            <Route path="/test-glass-effects" element={<TestGlassEffects />} />
            <Route path="/test-webview" element={<TestWebView />} />
            <Route path="/test-interval-tracking" element={<TestIntervalTracking />} />
            <Route path="/test-webview-intervals" element={<TestWebViewIntervals />} />
            <Route path="/webview-demo" element={<WebViewDemo />} />
            <Route path="/test-ios-pen" element={<TestIOSPen />} />
            
            {/* 404 Fallback */}
            <Route path="*" element={
              <div className="flex items-center justify-center h-screen bg-gray-50">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-8">Sayfa bulunamadı</p>
                  <a href="/" className="text-blue-600 hover:text-blue-800 underline">Ana sayfaya dön</a>
                </div>
              </div>
            } />
          </Routes>
        </Suspense>
      </main>
    </AuthWrapper>
  );
};

function App() {
  // Initialize Firebase authentication
  useFirebaseAuth();
  
  // Initialize focus visible (doesn't need router)
  useFocusVisible();
  
  // Initialize accessibility checklist
  useA11yChecklist();
  
  // Debug: Log any window modifications for WebView integration
  useEffect(() => {
    console.log('App: Initial window check:', {
      hasWhiteboardExternalProps: !!(window as any).whiteboardExternalProps,
      hasEdesisTahtaConfig: !!(window as any).edesisTahtaConfig,
      hasReactNativeWebView: !!(window as any).ReactNativeWebView
    });
    
    // Set up a MutationObserver to watch for window changes (for debugging)
    const checkWindowChanges = () => {
      if ((window as any).edesisTahtaConfig && !(window as any).__edesisTahtaConfigLogged) {
        console.log('App: edesisTahtaConfig detected on window!', (window as any).edesisTahtaConfig);
        (window as any).__edesisTahtaConfigLogged = true;
      }
      if ((window as any).whiteboardExternalProps && !(window as any).__whiteboardExternalPropsLogged) {
        console.log('App: whiteboardExternalProps detected on window!', (window as any).whiteboardExternalProps);
        (window as any).__whiteboardExternalPropsLogged = true;
      }
    };
    
    // Check immediately and then periodically
    checkWindowChanges();
    const interval = setInterval(checkWindowChanges, 100);
    
    // Clean up after 5 seconds
    setTimeout(() => clearInterval(interval), 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Run localStorage migration on app startup
  useEffect(() => {
    try {
      QuestionDataLocalStorage.migrateToNewFormat();
    } catch (error) {
      console.error('Failed to migrate localStorage keys:', error);
    }
  }, []);
  
  // Setup responsive detection
  useEffect(() => {
    const { detectAndSetMode } = useUIStore.getState();
    
    // Detect on mount
    detectAndSetMode();
    
    // Re-detect on resize
    const handleResize = () => {
      detectAndSetMode();
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  // Temporarily disable StrictMode to avoid double renders
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <WebViewProvider>
          <Router>
            <AppContent />
          </Router>
        </WebViewProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;